import {Component, computed, inject, signal} from '@angular/core';
import {FinancesPeriodStore} from '../../services/finances-period.store';
import {PeriodNavigatorComponent, PeriodRange} from '../../../../components/period-navigator/period-navigator';
import {Router} from '@angular/router';
import {FormControl} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {debounceTime} from 'rxjs';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {lastValueFrom} from 'rxjs';
import {TableModule} from 'primeng/table';
import {TranslatePipe} from '@ngx-translate/core';
import {CurrencyPipe, DatePipe} from '@angular/common';
import {CreditNoteService, CreditNoteStateId, CreditNoteSummary as CreditNoteSummaryModel} from '../../../../../api';
import {QueryKey} from '../../../../constants/query-key';
import {RouteUri} from '../../../../constants/route-uri';
import {Search} from '../../../../components/search/search';
import {ElciaButton} from '../../../../components/buttons/button/button';

@Component({
  selector: 'elcia-credit-note-summary',
  imports: [PeriodNavigatorComponent, TableModule, TranslatePipe, DatePipe, CurrencyPipe, Search, ElciaButton],
  templateUrl: './credit-note-summary.html',
  styleUrl: './credit-note-summary.scss',
})
export class CreditNoteSummary {
  protected periodStore = inject(FinancesPeriodStore);
  private creditNoteService = inject(CreditNoteService);
  private router = inject(Router);

  protected readonly CreditNoteStateId = CreditNoteStateId;

  protected readonly searchControl = new FormControl('', {nonNullable: true});
  private readonly search = toSignal(
    this.searchControl.valueChanges.pipe(debounceTime(300)),
    {initialValue: ''},
  );

  private get hasDates(): boolean {
    const {startDate, endDate} = this.periodStore.period();
    return startDate != null && endDate != null;
  }

  private readonly creditNotesQuery = injectQuery(() => ({
    queryKey: [QueryKey.CREDIT_NOTES, this.periodStore.period().startDate, this.periodStore.period().endDate, this.search()],
    queryFn: () => lastValueFrom(this.creditNoteService.getCreditNotes(
      this.periodStore.period().startDate,
      this.periodStore.period().endDate,
      [],
      [],
      [],
      this.search() || null,
    )),
    enabled: this.hasDates,
  }));

  protected readonly creditNotes = computed(() => this.creditNotesQuery.data()?.creditNotes ?? []);

  protected setPeriod($event: PeriodRange) {
    this.periodStore.period.set($event);
  }

  protected navToForm(): void {
    this.router.navigate([RouteUri.CREDIT_NOTE_FORM]);
  }

  protected isDraft(creditNote: CreditNoteSummaryModel): boolean {
    return creditNote.stateName === CreditNoteStateId.DRAFT;
  }

  // A draft credit note can be reopened to be previewed and sent; a sent one is read-only.
  protected select(creditNote: CreditNoteSummaryModel): void {
    if (this.isDraft(creditNote)) {
      this.router.navigate([RouteUri.CREDIT_NOTE_FORM, 'edit', creditNote.id]);
    }
  }
}
